<script setup lang="ts">
import type { InfoTextProps } from "./InfoText.props"
import { md, lg } from "~/utils/breakpoints"
import { ref } from "vue"

const props = defineProps<InfoTextProps>()

const isExpanded = ref(false)
const MAX_CHART = ref<number | null>(null)
const text = props.text

const isVisibleShowMore = (description: string) => {
  if (!MAX_CHART.value || !description?.length) {
    return false
  }
  const result = description?.length > MAX_CHART?.value
  return result
}

switch (true) {
  case lg.value:
    MAX_CHART.value = 200
    break
  case md.value:
    MAX_CHART.value = 180
    break
  default:
    MAX_CHART.value = 90
}

const toggleFullDescription = () => {
  isExpanded.value = !isExpanded.value
}

const truncateString = (str: string, num: number) => {
  if (str?.length <= num) {
    return str
  }
  return isExpanded.value ? str : str.slice(0, num)
}

const postProcessContent = (message: string) => {
  if (message && MAX_CHART.value) {
    return truncateString(message, MAX_CHART.value)
  }
}

const setSuffixText = () => (!isExpanded.value ? "..." : "")
</script>

<template>
  <div
    v-if="!!text?.length"
    @click="isVisibleShowMore(text) ? toggleFullDescription() : null"
    class="mb-4"
    :class="{
      'cursor-pointer': isVisibleShowMore(text)
    }"
  >
    <div v-if="MAX_CHART" class="flex items-center">
      <UtilsMarkdown
        :class="{
          'md:line-clamp-1': isVisibleShowMore(text),
          'line-clamp-2': !isExpanded,
          'line-clamp-none md:line-clamp-none': isExpanded
        }"
        class="flex-1 overflow-hidden"
        :content="text"
        :postProcessContent="postProcessContent"
        :suffixText="setSuffixText()"
        useLinkStyle
      />
      <button
        :class="{
          'rotate-0': !isExpanded,
          'rotate-180': isExpanded
        }"
        aria-label="toggle visibility"
        class="ml-2 transform transition-transform duration-300"
        v-if="isVisibleShowMore(text)"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-4 w-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>
    </div>
    <SkeletonAnimatedPlaceholder
      v-else
      height="24px"
      width="100%"
      class="mb-4"
    />
  </div>
</template>
